import React, { useEffect, useState, useMemo } from "react";
import "./ExpertCard.css";
import ContactModal from "../ContactModal/ContactModal";
import { useIsMobile } from "../../hooks/useIsMobile";
import {
  hydrateExpertFromPreload,
  extractExpertId,
  getPreloadedExpertById,
} from "../Data/expert-pre-load-database";

// Extract tags with prioritization logic
const extractExpertiseTags = (expert = {}) => {
  const tags = expert.tags || [];
  const prioritized = [];
  const fallback = [];

  const params = new URLSearchParams(window.location.search);
  const keywords = [
    params.get("keyword_primary_1"),
    params.get("keyword_primary_2"),
    params.get("keyword_primary_3"),
    params.get("keyword_primary_4"),
    params.get("keyword_primary_5"),
    params.get("keyword_primary_6"),
    params.get("keyword_secondary_1"),
    params.get("keyword_secondary_3"),
    params.get("keyword_secondary_6"),
    params.get("keyword_secondary_7"),
    params.get("keyword_secondary_8"),
    params.get("keyword_secondary_9"),
  ]
    .filter(Boolean)
    .map((k) => k.toLowerCase());

  for (const tag of tags) {
    const name = tag.hashtag_name?.toLowerCase() || "";
    if (keywords.some((kw) => name.includes(kw))) {
      prioritized.push(tag.hashtag_name);
    } else {
      fallback.push(tag.hashtag_name);
    }
  }

  return prioritized.length ? prioritized : fallback.slice(0, 3);
};

// Async image validator
const checkImageExists = (url) => {
  return new Promise((resolve) => {
    if (!url) return resolve(false);
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

// Special ID check
const isKingsHawaiian = (expert) => {
  const id = expert?.global_id ?? expert?.globalId ?? expert?.id ?? null;
  return String(id) === "65718";
};

// Truncate helper
const truncate = (text = "", max = 35) => {
  if (!text) return "";
  return text.length > max ? text.slice(0, max) + "..." : text;
};

const ExpertCard = ({ expert = {}, loading, onTagClick, sendToIntercom = false }) => {
  const hydratedExpert = useMemo(() => hydrateExpertFromPreload(expert), [expert]);

  const preloadedForThisId = useMemo(() => {
    const id = extractExpertId(hydratedExpert);
    return !!getPreloadedExpertById(id) || !!expert.__preloaded;
  }, [hydratedExpert, expert]);

  const [hasValidImage, setHasValidImage] = useState(preloadedForThisId);
  const [showModal, setShowModal] = useState(false);

  const imgUrl = hydratedExpert.profile_image_url;

  useEffect(() => {
    if (preloadedForThisId) {
      setHasValidImage(true);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!imgUrl || imgUrl.includes("dicebear")) {
        if (!cancelled) setHasValidImage(false);
        return;
      }
      const ok = await checkImageExists(imgUrl);
      if (!cancelled) setHasValidImage(ok);
    })();
    return () => {
      cancelled = true;
    };
  }, [imgUrl, preloadedForThisId]);

  const isMobile = useIsMobile();

  if (loading) {
    return (
      <div className="expert-card loading">
        <div className="card-header"></div>
        <div className="profile-section">
          <div className="profile-image loading-pulse"></div>
          <div className="profile-info">
            <div className="loading-pulse" style={{ width: "120px", height: "20px" }} aria-label="Loading name"></div>
            <p className="title loading-pulse" style={{ width: "80px", height: "16px" }}></p>
          </div>
        </div>
        <div className="expert-on">
          <div className="tags">
            {[1, 2, 3].map((idx) => (
              <span key={idx} className="tag loading-pulse" style={{ width: "60px" }}></span>
            ))}
          </div>
        </div>
        <div className="latest-section">
          <p className="loading-pulse" style={{ width: "100%", height: "40px" }}></p>
        </div>
        <button className="contact-button loading-pulse"></button>
      </div>
    );
  }

  if (!hasValidImage) return null;

  const tags = extractExpertiseTags(hydratedExpert);
  const name = hydratedExpert.name || "Unknown Expert";

  const rawTitle =
    hydratedExpert.featured_title_at_company ||
    hydratedExpert.headline ||
    hydratedExpert.featured_company_name ||
    "-";

  const displayTitle = isKingsHawaiian(hydratedExpert)
    ? "Spokesperson at King's Hawaiian, Celebrity Chef"
    : isMobile
      ? truncate(String(rawTitle), 36)
      : String(rawTitle);

  const description = hydratedExpert.description || "";

  const showFrequentSource = hydratedExpert.mediaCount > 5;
  const frequentSourceBadge =
    "https://qwotedstg.wpenginepowered.com/wp-content/uploads/2025/06/Frequent-Source.png";

  const handleTagClick = (tag) => {
    const formatted = tag.replace(/^#/, "").replace(/([A-Z])/g, " $1").trim();
    onTagClick && onTagClick(formatted);
  };

  return (
    <div className="expert-card">
      <div className="card-header"></div>
      <div className="profile-section">
        <div className="profile-image-wrapper">
          <img src={imgUrl} alt={name} className="profile-image" />
          {showFrequentSource && (
            <img src={frequentSourceBadge} alt="Frequent Source" className="frequent-source-badge" />
          )}
        </div>
        <div className="profile-info">
          <h3>{name}</h3>
          <p className="title">{displayTitle}</p>
        </div>
      </div>

      {tags.length > 0 && (
        <div className="expert-on">
          <div className="tags">
            {tags.map((tag, idx) => (
              <span key={idx} className="tag" onClick={() => handleTagClick(tag)}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {description && (
        <div className="latest-section">
          <p>{description.substring(0, 180)}...</p>
        </div>
      )}

      <button
        className="contact-button"
        onClick={() => setShowModal(true)}
      >
        Contact
      </button>

      <ContactModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        expertName={name}
      />
    </div>
  );
};

export default ExpertCard;
